import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import StudentHeader from '../components/StudentHeader';
import { studentFetch } from '../utils/authFetch';
import * as THREE from 'three';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const BADGE_EMOJI = {
  ACH_001:'🚀', ACH_002:'🔥', ACH_003:'⭐', ACH_004:'💪',
  ACH_005:'📚', ACH_006:'🎯', ACH_007:'💯', ACH_008:'🌍',
  ACH_009:'🏅', ACH_010:'🃏', ACH_011:'⚡', ACH_012:'🎟️',
  ACH_013:'🚀', ACH_014:'🔥', ACH_015:'⭐', ACH_016:'📚',
  ACH_017:'🧠', ACH_018:'🥇', ACH_019:'🎓', ACH_020:'✨',
  ACH_021:'🏆', ACH_022:'📖', ACH_023:'🏅', ACH_024:'📈',
};
const DEFAULT_EMOJI = '🎖️';

const T = {
  ar: {
    title:         'الإنجازات',
    stage1:        'المرحلة الأولى · ص١-٦',
    stage2:        'المرحلة الثانية · ص٧-٩',
    totalXp:       'مجموع النقاط',
    currentStreak: 'السلسلة الحالية',
    bestStreak:    'أفضل سلسلة',
    earned:        'إنجازاتك',
    locked:        'قيد الإنجاز',
    progressLabel: 'إنجازات مكتسبة:',
    newBadge:      'جديد',
    loading:       'جاري التحميل...',
    error:         'حدث خطأ، حاول مرة أخرى',
    stage3Title:   'تتبع التقدم الأكاديمي',
    stage3Sub:     'يمكنك متابعة تقدمك في صفحة النتائج',
    stage3Btn:     'عرض النتائج ←',
    back:          '← رجوع',
  },
  en: {
    title:         'Achievements',
    stage1:        'Stage 1 · G1-6',
    stage2:        'Stage 2 · G7-9',
    totalXp:       'Total XP',
    currentStreak: 'Current Streak',
    bestStreak:    'Best Streak',
    earned:        'Achievements Earned',
    locked:        'Locked',
    progressLabel: 'Achievements earned:',
    newBadge:      'NEW',
    loading:       'Loading...',
    error:         'Something went wrong, try again',
    stage3Title:   'Academic progress tracking',
    stage3Sub:     'Milestone progress is available in your Results page',
    stage3Btn:     'View Results →',
    back:          'Back →',
  },
};

function formatEarnedDate(iso, lang) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (lang === 'ar') {
      const months = ['يناير','فبراير','مارس','أبريل','مايو','يونيو',
                      'يوليو','أغسطس','سبتمبر','أكتوبر','نوفمبر','ديسمبر'];
      return `${d.getDate()} ${months[d.getMonth()]}`;
    }
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

function isNew(iso) {
  if (!iso) return false;
  try {
    return (Date.now() - new Date(iso).getTime()) < 7 * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

export default function Achievements() {
  const { lang } = useLanguage();
  const t        = T[lang] || T.ar;
  const dir      = lang === 'ar' ? 'rtl' : 'ltr';
  const navigate = useNavigate();

  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('student_token');
    studentFetch(`${API}/student/achievements`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r ? r.json() : null)
      .then(json => {
        if (!json) return;
        if (json.stage !== undefined) {
          setData(json);
        } else {
          setError(t.error);
        }
      })
      .catch(() => setError(t.error))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const stickyHeader = {
    position: 'sticky', top: 0, zIndex: 100,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '12px 24px',
    background: 'rgba(255,255,255,0.85)',
    backdropFilter: 'blur(12px)',
    borderBottom: '1px solid #e5e7eb',
  };
  const headerBackBtn = {
    background: 'transparent', border: '1px solid #e5e7eb',
    borderRadius: 10, padding: '6px 16px', fontSize: 13,
    fontWeight: 600, color: '#1e3a5f', cursor: 'pointer',
    fontFamily: 'Sora, sans-serif',
  };

  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(window.innerWidth, window.innerHeight);
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(55, window.innerWidth / window.innerHeight, 0.1, 100);
    camera.position.z = 7;
    scene.add(new THREE.AmbientLight(0xffffff, 0.4));
    const L1 = new THREE.DirectionalLight(0xffffff, 1.2); L1.position.set(6, 8, 4); scene.add(L1);
    const L2 = new THREE.DirectionalLight(0xffffff, 0.6); L2.position.set(-5, -3, 4); scene.add(L2);
    const meshes = [];
    [
      [new THREE.IcosahedronGeometry(0.36, 0), -4, 2.5, -1.2],
      [new THREE.IcosahedronGeometry(0.24, 0), 3.6, 2, -2],
      [new THREE.OctahedronGeometry(0.30, 0), -3, -1.8, -1.5],
      [new THREE.OctahedronGeometry(0.20, 0), 2.8, -2.2, -1],
      [new THREE.TetrahedronGeometry(0.26, 0), 0.7, 2.8, -1.8],
      [new THREE.TetrahedronGeometry(0.16, 0), -1.4, -2.6, -1.4],
      [new THREE.IcosahedronGeometry(0.14, 0), 4, 0.5, -0.6],
      [new THREE.OctahedronGeometry(0.12, 0), -4, -0.7, -0.6],
      [new THREE.IcosahedronGeometry(0.11, 0), 1.8, -3, -0.9],
      [new THREE.TetrahedronGeometry(0.13, 0), -2, 3, -0.9],
    ].forEach(([G, x, y, z]) => {
      const mat = new THREE.MeshPhongMaterial({ color: 0x34D399, transparent: true, opacity: 0.22, shininess: 120, specular: new THREE.Color(0xffffff) });
      const m = new THREE.Mesh(G, mat);
      m.position.set(x, y, z);
      m.rotation.set(Math.random() * Math.PI * 2, Math.random() * Math.PI * 2, Math.random() * Math.PI * 2);
      m._oy = y; m._fs = 0.2 + Math.random() * 0.5; m._fa = 0.12 + Math.random() * 0.22;
      m._rx = (0.002 + Math.random() * 0.006) * (Math.random() > 0.5 ? 1 : -1);
      m._ry = (0.002 + Math.random() * 0.005) * (Math.random() > 0.5 ? 1 : -1);
      m._ph = Math.random() * Math.PI * 2;
      scene.add(m); meshes.push(m);
    });
    const PC = 120;
    const pp = new Float32Array(PC * 3);
    const pv = [];
    for (let i = 0; i < PC; i++) {
      pp[i * 3] = (Math.random() - 0.5) * 16;
      pp[i * 3 + 1] = (Math.random() - 0.5) * 12;
      pp[i * 3 + 2] = (Math.random() - 0.5) * 5 - 1.5;
      pv.push({ vx: (Math.random() - 0.5) * 0.007, vy: (Math.random() - 0.5) * 0.007 });
    }
    const pGeo = new THREE.BufferGeometry();
    pGeo.setAttribute('position', new THREE.BufferAttribute(pp, 3));
    const pMat = new THREE.PointsMaterial({ color: 0x34D399, size: 0.07, transparent: true, opacity: 0.5, sizeAttenuation: true });
    scene.add(new THREE.Points(pGeo, pMat));
    const lp = new Float32Array(PC * PC * 6);
    const lGeo = new THREE.BufferGeometry();
    lGeo.setAttribute('position', new THREE.BufferAttribute(lp, 3));
    const lMesh = new THREE.LineSegments(lGeo, new THREE.LineBasicMaterial({ color: 0x34D399, transparent: true, opacity: 0.1 }));
    scene.add(lMesh);
    let mx = 0, my = 0;
    const onMM = (e) => { mx = (e.clientX / window.innerWidth - 0.5) * 2; my = (e.clientY / window.innerHeight - 0.5) * 2; };
    window.addEventListener('mousemove', onMM);
    const onResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', onResize);
    let t = 0, li = 0, animId;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      t += 0.007;
      meshes.forEach(m => {
        m.rotation.x += m._rx; m.rotation.y += m._ry;
        m.position.y = m._oy + Math.sin(t * m._fs + m._ph) * m._fa;
      });
      for (let i = 0; i < PC; i++) {
        pp[i * 3] += pv[i].vx; pp[i * 3 + 1] += pv[i].vy;
        if (Math.abs(pp[i * 3]) > 8) pv[i].vx *= -1;
        if (Math.abs(pp[i * 3 + 1]) > 6) pv[i].vy *= -1;
      }
      pGeo.attributes.position.needsUpdate = true;
      li = 0;
      for (let i = 0; i < PC; i++) for (let j = i + 1; j < PC; j++) {
        const dx = pp[i * 3] - pp[j * 3], dy = pp[i * 3 + 1] - pp[j * 3 + 1];
        if (dx * dx + dy * dy < 5) {
          lp[li++] = pp[i * 3]; lp[li++] = pp[i * 3 + 1]; lp[li++] = pp[i * 3 + 2];
          lp[li++] = pp[j * 3]; lp[li++] = pp[j * 3 + 1]; lp[li++] = pp[j * 3 + 2];
        }
      }
      lGeo.attributes.position.needsUpdate = true;
      lGeo.setDrawRange(0, li / 3);
      camera.position.x += (mx * 0.3 - camera.position.x) * 0.032;
      camera.position.y += (-my * 0.18 - camera.position.y) * 0.032;
      camera.lookAt(0, 0, 0);
      renderer.render(scene, camera);
    };
    animate();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('mousemove', onMM);
      window.removeEventListener('resize', onResize);
      renderer.dispose();
    };
  }, []);

  // Wrapper that ONLY adds the z-indexed content layer.
  // The canvas is rendered separately via portal to document.body
  // so it persists across branch switches (loading → main, etc.)
  const WithCanvas = ({ children, rootStyle, dir: dirProp }) => (
    <div dir={dirProp} style={{ ...rootStyle, position: 'relative' }}>
      <div style={{ position: 'relative', zIndex: 10 }}>
        {children}
      </div>
    </div>
  );

  // Single canvas rendered to document.body — stays mounted across
  // all branch switches (loading/error/stage3/main) so Three.js
  // never loses its DOM node reference.
  const CanvasPortal = () =>
    typeof document !== 'undefined'
      ? createPortal(
          <canvas
            ref={canvasRef}
            style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none' }}
          />,
          document.body
        )
      : null;

  if (loading) {
    return (
      <>
        <CanvasPortal />
        <WithCanvas
          dirProp={dir}
          rootStyle={{ display:'flex', alignItems:'center', justifyContent:'center',
                       minHeight:'100vh', background:'transparent' }}
        >
          <div style={{ width:40, height:40, border:'4px solid #d1fae5',
                        borderTopColor:'#059669', borderRadius:'50%',
                        animation:'ach-spin 0.8s linear infinite' }} />
          <style>{`@keyframes ach-spin{to{transform:rotate(360deg)}}`}</style>
        </WithCanvas>
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <CanvasPortal />
        <WithCanvas
          dirProp={dir}
          rootStyle={{ display:'flex', alignItems:'center', justifyContent:'center',
                       minHeight:'100vh', background:'transparent', padding:24 }}
        >
          <div style={{ background:'#fff', border:'2px solid #FCA5A5', borderRadius:20,
                        padding:'32px 40px', textAlign:'center', color:'#dc2626', fontSize:15,
                        boxShadow:'0 3px 0 #FCA5A5' }}>
            {error || t.error}
          </div>
        </WithCanvas>
      </>
    );
  }

  const { stage, student_name, total_xp, current_streak, longest_streak, achievements } = data;

  /* ── Stage 3 view ── */
  if (stage === '3') {
    return (
      <>
        <CanvasPortal />
        <WithCanvas
          dirProp={dir}
          rootStyle={{ minHeight:'100vh', display:'flex', alignItems:'center',
                       justifyContent:'center', padding:24,
                       background:'transparent',
                       fontFamily:'Sora, sans-serif' }}
        >
          <div style={{ textAlign:'center', padding:'40px 0' }}>
            <div style={{ fontSize:64, background:'#f3f4f6', borderRadius:20,
                          width:96, height:96, display:'flex', alignItems:'center',
                          justifyContent:'center', margin:'0 auto 24px' }}>📊</div>
            <div style={{ fontSize:20, fontWeight:700, color:'#1e3a5f', marginBottom:10 }}>
              {t.stage3Title}
            </div>
            <div style={{ fontSize:14, color:'#6b7280', marginBottom:28, lineHeight:1.6 }}>
              {t.stage3Sub}
            </div>
            <button
              onClick={() => navigate('/student/results')}
              style={{ background:'#059669', color:'#fff', border:'none', borderRadius:12,
                       padding:'12px 28px', fontSize:15, fontWeight:600, cursor:'pointer',
                       fontFamily:'Sora, sans-serif' }}
            >
              {t.stage3Btn}
            </button>
          </div>
        </WithCanvas>
      </>
    );
  }

  const earned = [...achievements.filter(a => a.earned)]
    .sort((a, b) => new Date(b.earned_at || 0) - new Date(a.earned_at || 0));
  const locked = achievements.filter(a => !a.earned);
  const total  = achievements.length;
  const earnedCount = earned.length;
  const fillPct = total > 0 ? Math.round((earnedCount / total) * 100) : 0;

  const stageLabel = stage === '1' ? t.stage1 : t.stage2;

  const EMOJI_ANIM = {
    ACH_001: '',
    ACH_002: 'flame-sway 1s ease-in-out infinite',
    ACH_003: 'star-spin-glow 3s linear infinite',
    ACH_004: 'muscle-pulse 1.5s ease-in-out infinite',
    ACH_005: 'book-flip 2s ease-in-out infinite',
    ACH_006: 'flash-glow 1.5s ease-in-out infinite',
    ACH_007: 'star-spin-glow 3s linear infinite',
    ACH_008: 'slow-spin 4s linear infinite',
    ACH_009: 'medal-swing 2s ease-in-out infinite',
    ACH_010: 'book-flip 2s ease-in-out infinite',
    ACH_011: 'flash-glow 1s ease-in-out infinite',
    ACH_012: 'sparkle 2s ease-in-out infinite',
    ACH_013: '',
    ACH_014: 'flame-sway 1s ease-in-out infinite',
    ACH_015: 'flash-glow 1.5s ease-in-out infinite',
    ACH_016: 'book-flip 2s ease-in-out infinite',
    ACH_017: 'heartbeat 1.8s ease-in-out infinite',
    ACH_018: 'medal-swing 2s ease-in-out infinite',
    ACH_019: 'cap-jump 2s ease-in-out infinite',
    ACH_020: 'sparkle 1.5s ease-in-out infinite',
    ACH_021: 'trophy-shimmer 2s ease-in-out infinite',
    ACH_022: 'slow-spin 4s linear infinite',
    ACH_023: 'medal-swing 2s ease-in-out infinite',
    ACH_024: 'arrow-up 1.5s ease-in-out infinite',
  };

  return (
    <>
      <CanvasPortal />
      <WithCanvas
        dirProp={dir}
        rootStyle={{ minHeight:'100vh', fontFamily:'Sora, sans-serif',
                     background:'transparent',
                     padding:24 }}
      >
      <style>{`
        @keyframes ach-fadein {
          from { opacity:0; transform:translateY(12px); }
          to   { opacity:1; transform:translateY(0); }
        }

        @keyframes star-spin-glow {
          0%   { transform:rotate(0deg);   filter:brightness(1) drop-shadow(0 0 0px gold); }
          50%  { transform:rotate(180deg); filter:brightness(1.6) drop-shadow(0 0 8px gold); }
          100% { transform:rotate(360deg); filter:brightness(1) drop-shadow(0 0 0px gold); }
        }
        @keyframes flame-sway {
          0%,100% { transform: rotate(-8deg) scale(1); }
          50%      { transform: rotate(8deg) scale(1.1); }
        }
        @keyframes book-flip {
          0%,100% { transform: scaleX(1); }
          50%      { transform: scaleX(0.6); }
        }
        @keyframes muscle-pulse {
          0%,100% { transform: scale(1); }
          40%      { transform: scale(1.25) rotate(-5deg); }
          60%      { transform: scale(1.2) rotate(5deg); }
        }
        @keyframes flash-glow {
          0%,100% { filter: brightness(1); }
          50%      { filter: brightness(1.5) drop-shadow(0 0 6px #facc15); }
        }
        @keyframes heartbeat {
          0%,100% { transform: scale(1); }
          14%      { transform: scale(1.2); }
          28%      { transform: scale(1); }
          42%      { transform: scale(1.15); }
          56%      { transform: scale(1); }
        }
        @keyframes medal-swing {
          0%,100% { transform: rotate(-12deg); }
          50%      { transform: rotate(12deg); }
          transform-origin: top center;
        }
        @keyframes cap-jump {
          0%,100% { transform: translateY(0); }
          40%      { transform: translateY(-10px) rotate(-5deg); }
          60%      { transform: translateY(-8px) rotate(3deg); }
        }
        @keyframes sparkle {
          0%,100% { transform: scale(1) rotate(0deg);    opacity:1; }
          25%      { transform: scale(1.3) rotate(15deg); opacity:0.7; }
          50%      { transform: scale(0.8) rotate(-10deg);opacity:1; }
          75%      { transform: scale(1.2) rotate(20deg); opacity:0.8; }
        }
        @keyframes trophy-shimmer {
          0%,100% { filter: brightness(1) drop-shadow(0 0 0px gold); }
          50%      { filter: brightness(1.4) drop-shadow(0 0 10px gold); }
        }
        @keyframes slow-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes arrow-up {
          0%,100% { transform: translateY(0); }
          50%      { transform: translateY(-8px); }
        }
        @keyframes card-pop {
          0%   { opacity:0; transform:scale(0.7) translateY(16px); }
          70%  { transform:scale(1.05) translateY(-2px); }
          100% { opacity:1; transform:scale(1) translateY(0); }
        }
        @keyframes locked-in {
          0%   { opacity:0; transform:translateY(12px); }
          100% { opacity:0.5; transform:translateY(0); }
        }
        .ach-card-earned { transition: transform .15s, box-shadow .15s; box-shadow: 0 5px 0 #A7F3D0, 0 10px 24px rgba(5,150,105,.07); }
        .ach-card-earned:hover { transform:translateY(-6px) !important; box-shadow:0 5px 0 #6EE7B7, 0 18px 36px rgba(5,150,105,.2) !important; border-color:#6EE7B7 !important; }
        .ach-card-earned:active { transform:translateY(3px) !important; box-shadow:0 1px 0 #A7F3D0 !important; }
        .ach-stat-card { transition: transform .15s, box-shadow .15s; box-shadow: 0 4px 0 #E5E7EB, 0 8px 20px rgba(0,0,0,.05); }
        .ach-stat-card:hover { transform: translateY(-4px); box-shadow: 0 4px 0 #D1D5DB, 0 14px 28px rgba(0,0,0,.08); }
        @media (max-width: 767px) {
          .ach-badge-grid { grid-template-columns: repeat(2,1fr) !important; }
          .ach-stats-row  { flex-direction: column; }
        }
        @media (min-width: 768px) and (max-width: 1023px) {
          .ach-badge-grid { grid-template-columns: repeat(3,1fr) !important; }
        }
        @media (min-width: 1024px) {
          .ach-badge-grid { grid-template-columns: repeat(4,1fr) !important; }
        }
      `}</style>

      <StudentHeader
        title={lang === 'ar' ? 'الإنجازات' : 'Achievements'}
        showBack
        onBack={() => window.history.back()}
        lang={lang}
      />

<div style={{ maxWidth:900, margin:'0 auto', animation:'ach-fadein 0.4s ease' }}>

        {/* ── Header ── */}
        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:24, flexWrap:'wrap' }}>
          <div style={{ fontSize:20, fontWeight:700, color:'#1e3a5f' }}>{t.title}</div>
          <span style={{ background:'#e6f4ee', color:'#059669', fontSize:11, fontWeight:600,
                         borderRadius:20, padding:'3px 10px' }}>
            {stageLabel}
          </span>
        </div>

        {/* ── Stats row ── */}
        <div className="ach-stats-row" style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:20 }}>
          {[
            { icon:'⭐', value: total_xp.toLocaleString(), label: t.totalXp,       color:'#D97706' },
            { icon:'🔥', value: current_streak,            label: t.currentStreak, color:'#EF4444' },
            { icon:'🏅', value: longest_streak,            label: t.bestStreak,    color:'#6366F1' },
          ].map((s, i) => (
            <div key={i} className="ach-stat-card"
                 style={{ flex:'1 1 140px', background:'#fff', border:'2px solid #E5E7EB',
                          borderRadius:18, padding:'12px 16px', display:'flex',
                          alignItems:'center', gap:12 }}>
              <span style={{ fontSize:26 }}>{s.icon}</span>
              <div>
                <div style={{ fontSize:22, fontWeight:800, color:s.color, lineHeight:1 }}>
                  {s.value}
                </div>
                <div style={{ fontSize:11, color:'#6b7280', marginTop:3 }}>{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* ── Progress bar ── */}
        <div style={{ background:'#f3f4f6', borderRadius:18, padding:'14px 18px', marginBottom:28, border:'1px solid #E5E7EB', boxShadow:'0 2px 0 #E5E7EB' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                        marginBottom:10 }}>
            <span style={{ fontSize:13, color:'#374151', fontWeight:600 }}>{t.progressLabel}</span>
            <span style={{ fontSize:13, fontWeight:700, color:'#059669' }}>
              {earnedCount} / {total}
            </span>
          </div>
          <div style={{ background:'#e5e7eb', borderRadius:99, height:10, overflow:'hidden', boxShadow:'inset 0 1px 2px rgba(0,0,0,.08)' }}>
            <div style={{ width:`${fillPct}%`, height:'100%', borderRadius:99,
                          background:'linear-gradient(90deg, #059669, #10b981)',
                          transition:'width 0.6s ease',
                          position:'relative', overflow:'hidden' }} />
          </div>
        </div>

        {/* ── Earned section ── */}
        {earned.length > 0 && (
          <div style={{ marginBottom:32 }}>
            <div style={{ fontSize:16, fontWeight:700, color:'#1e3a5f', marginBottom:16 }}>
              🏆 {t.earned}
            </div>
            <div className="ach-badge-grid" style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(140px, 1fr))',
                          gap:12 }}>
              {earned.map((a, index) => {
                const emoji = BADGE_EMOJI[a.code] || DEFAULT_EMOJI;
                const name  = lang === 'ar' ? a.name_ar : a.name_en;
                const fresh = isNew(a.earned_at);
                return (
                  <div key={a.id} className="ach-card-earned"
                       style={{ position:'relative', background:'#fff',
                                border:'2px solid #A7F3D0', borderRadius:20,
                                padding:'16px 12px', textAlign:'center',
                                opacity:0,
                                animation:`card-pop 0.5s cubic-bezier(0.34,1.56,0.64,1) ${index * 0.08}s forwards` }}>
                    {fresh && (
                      <span style={{ position:'absolute', top:8, insetInlineEnd:8,
                                     background:'#ef4444', color:'#fff', fontSize:9,
                                     fontWeight:700, padding:'2px 6px', borderRadius:99 }}>
                        {t.newBadge}
                      </span>
                    )}
                    <div style={{ width:52, height:52, borderRadius:16, margin:'0 auto 10px',
                                  background:'linear-gradient(135deg, #059669, #10b981)',
                                  display:'flex', alignItems:'center', justifyContent:'center',
                                  fontSize:24, overflow:'hidden' }}>
                      <span style={{ display:'inline-block', animation: EMOJI_ANIM[a.code] || '' }}>
                        {emoji}
                      </span>
                    </div>
                    <div style={{ fontSize:12, fontWeight:600, color:'#1e3a5f',
                                  marginBottom:4, lineHeight:1.3 }}>
                      {name}
                    </div>
                    <div style={{ fontSize:11, fontWeight:700, color:'#D97706', marginBottom:3 }}>
                      +{a.xp_reward} XP
                    </div>
                    <div style={{ fontSize:10, color:'#9ca3af' }}>
                      {formatEarnedDate(a.earned_at, lang)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Locked section ── */}
        {locked.length > 0 && (
          <div>
            <div style={{ fontSize:16, fontWeight:700, color:'#1e3a5f', marginBottom:16 }}>
              🔒 {t.locked}
            </div>
            <div className="ach-badge-grid" style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(140px, 1fr))',
                          gap:12 }}>
              {locked.map((a, index) => {
                const emoji = BADGE_EMOJI[a.code] || DEFAULT_EMOJI;
                const name  = lang === 'ar' ? a.name_ar : a.name_en;
                return (
                  <div key={a.id}
                       style={{ background:'#fff', border:'2px solid #E5E7EB',
                                borderRadius:20, padding:'16px 12px', textAlign:'center',
                                opacity:0, filter:'grayscale(0.5)',
                                boxShadow:'0 3px 0 #E5E7EB',
                                animation:`locked-in 0.4s ease ${index * 0.06}s forwards` }}>
                    <div style={{ width:52, height:52, borderRadius:16, margin:'0 auto 10px',
                                  background:'#e5e7eb', display:'flex', alignItems:'center',
                                  justifyContent:'center', fontSize:24, position:'relative' }}>
                      {emoji}
                      <div style={{ position:'absolute', bottom:-4, insetInlineEnd:-4,
                                    width:18, height:18, borderRadius:'50%',
                                    background:'#9ca3af', display:'flex', alignItems:'center',
                                    justifyContent:'center', fontSize:9, color:'#fff',
                                    fontWeight:700 }}>
                        🔒
                      </div>
                    </div>
                    <div style={{ fontSize:12, fontWeight:600, color:'#1e3a5f',
                                  marginBottom:4, lineHeight:1.3 }}>
                      {name}
                    </div>
                    <div style={{ fontSize:10, color:'#9ca3af', lineHeight:1.4 }}>
                      {a.description}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </WithCanvas>
    </>
  );
}
